/**
 * Mock data — extracted from api.js for lazy-loading.
 * Contains all mock constants and the mockRequest() function.
 */

import type { UserSummary } from './types/users';
import type { CDRRecord } from './types/cdr';
import type { DepartmentSummary } from './types/departments';

interface MockUser extends UserSummary {
    mobile?: string;
    fwd_target?: string;
    fwd_timeout?: number;
}

interface MockCDR {
    id: string;
    src_user: string;
    dst_user: string;
    call_start: string;
    duration: number;
    disposition: string;
    direction: string;
    trunk?: string;
    recording_path?: string;
}

interface MockRingGroup {
    extension: string;
    name: string;
    strategy: string;
    members: number;
}

interface MockDepartment {
    id: string;
    name: string;
    member_count: number;
}

interface MockTrunkStats {
    today_calls: number;
    answered: number;
    missed: number;
    avg_duration: number;
    total_duration: number;
    status: string;
}

interface MockTrunk {
    id: string;
    name: string;
    host: string;
    protocol: string;
    status: string;
}

interface MockDaySchedule {
    enabled: boolean;
    start: string;
    end: string;
}

interface MockException {
    date: string;
    name: string;
    type: string;
}

interface MockOfficeHours {
    schedule: Record<string, MockDaySchedule>;
    exceptions: MockException[];
}

interface MockVoicemail {
    enabled: boolean;
    max_message_length: number;
    max_messages: number;
    min_message_length: number;
    max_greeting_length: number;
    format: string;
    email_notifications: boolean;
    email_body: boolean;
    delete_after_email: boolean;
    timezone: string;
    operator_extension: string;
    context: string;
}

interface MockSystem {
    services: Record<string, string>;
    active_calls: number;
    registered_users: number;
    total_users: number;
    disk_recordings: { used_gb: number; total_gb: number; path: string };
    version: string;
    components: Record<string, string>;
    domain: string;
    internal_ip: string;
    external_ip: string;
}

interface RequestOptions {
    method?: string;
    body?: any;
    headers?: Record<string, string>;
}

export const MOCK_USERS: MockUser[] = [
    {
        id: '1',
        full_name: 'Коновалюк Дмитро Вікторович',
        username: 'dk',
        email: 'dk@nir.gov.ua',
        extension: '1069',
        department_name: 'Київ',
        presence_state: 'available',
        line_active: true,
        is_admin: true,
        position: 'Системний адміністратор',
        mobile: '0631926001',
        fwd_target: '0631926001',
        fwd_timeout: 12,
    },
    {
        id: '2',
        full_name: 'Басіст Олександр Володимирович',
        username: 'alexander.basist',
        email: 'alexander.basist@nir.gov.ua',
        extension: '1081',
        department_name: 'Хмельницький',
        presence_state: 'available',
        line_active: true,
        position: 'Інспектор',
        mobile: '0960225067',
        fwd_target: '0960225067',
        fwd_timeout: 12,
    },
    {
        id: '3',
        full_name: 'Городинець Олександр Миколайович',
        username: 'alexander.gorodinets',
        email: 'alexander.gorodinets@nir.gov.ua',
        extension: '1079',
        department_name: 'Київ',
        presence_state: 'busy',
        line_active: true,
        position: 'Провідний науковий співробітник',
        mobile: '0679348994',
        fwd_target: '0679348994',
        fwd_timeout: 12,
    },
    {
        id: '4',
        full_name: 'Півень Олександр Петрович',
        username: 'alexander.piven',
        email: 'alexander.piven@nir.gov.ua',
        extension: '1057',
        department_name: 'Хмельницький',
        presence_state: 'away',
        line_active: true,
        position: 'Інспектор',
        mobile: '380677971074',
        fwd_target: '380677971074',
        fwd_timeout: 20,
    },
    {
        id: '5',
        full_name: 'Мартиненко Аліна Ігорівна',
        username: 'alina.martynenko',
        email: 'alina.martynenko@nir.gov.ua',
        extension: '1088',
        department_name: 'Київ',
        presence_state: 'dnd',
        line_active: true,
        position: 'Науковий співробітник',
        mobile: '0636446567',
        fwd_target: '0636446567',
        fwd_timeout: 15,
    },
    {
        id: '6',
        full_name: 'Єгорова Людмила Дмитрівна',
        username: 'egorova',
        email: 'egorova@nir.gov.ua',
        extension: '1042',
        department_name: 'Київ',
        presence_state: 'available',
        line_active: true,
        position: 'Начальник відділу',
        mobile: '0501234567',
    },
    {
        id: '7',
        full_name: 'Сопова Оксана Василівна',
        username: 'sopova',
        email: 'sopova@nir.gov.ua',
        extension: '1033',
        department_name: 'Київ',
        presence_state: 'available',
        line_active: true,
        position: 'Діловод',
        mobile: '0661234567',
    },
    {
        id: '8',
        full_name: 'Самойліч Альона Валеріївна',
        username: 'samoilich',
        email: 'samoilich@nir.gov.ua',
        extension: '1041',
        department_name: 'Київ',
        presence_state: 'offline',
        line_active: true,
        position: 'Діловод',
    },
    {
        id: '9',
        full_name: 'Сидоренко Володимир Сергійович',
        username: 'sydorenko',
        email: 'sydorenko@nir.gov.ua',
        extension: '1142',
        department_name: 'Київ',
        presence_state: 'available',
        line_active: true,
        position: 'Інспектор',
    },
    {
        id: '10',
        full_name: 'Турчина Людмила Анатоліївна',
        username: 'turchyna',
        email: 'turchyna@nir.gov.ua',
        extension: '1080',
        department_name: 'Київ',
        presence_state: 'business_trip',
        line_active: true,
        position: 'Інспектор',
    },
    {
        id: '11',
        full_name: 'Кондратенко Юлія Сергіївна',
        username: 'kondratenko.yu',
        email: 'kondratenko.yu@nir.gov.ua',
        extension: '1137',
        department_name: 'Київ',
        presence_state: 'available',
        line_active: true,
        position: 'Діловод',
    },
    {
        id: '12',
        full_name: 'Ковальчук Наталія Петрівна',
        username: 'kovalchuk',
        email: 'kovalchuk@nir.gov.ua',
        extension: '1091',
        department_name: 'Житомир',
        presence_state: 'offline',
        line_active: false,
        position: 'Інспектор',
    },
    {
        id: '13',
        full_name: 'Левченко Інна Леонідівна',
        username: 'levchenko',
        email: 'levchenko@nir.gov.ua',
        extension: '1029',
        department_name: 'Київ',
        presence_state: 'lunch',
        line_active: true,
        position: 'Спеціаліст із закупівель',
    },
    {
        id: '14',
        full_name: 'Кутковець Максим Леонідович',
        username: 'kutkovets',
        email: 'kutkovets@nir.gov.ua',
        extension: '1074',
        department_name: 'Київ',
        presence_state: 'available',
        line_active: true,
        position: 'Юрисконсульт',
    },
    {
        id: '15',
        full_name: 'Сімбаба Вікторія Вікторівна',
        username: 'simbaba',
        email: 'simbaba@nir.gov.ua',
        extension: '1151',
        department_name: 'Київ',
        presence_state: 'available',
        line_active: true,
        position: 'Спеціаліст з антикорупції',
    },
];

export const MOCK_CDR: MockCDR[] = [
    {
        id: 'c1',
        src_user: '0442987003',
        dst_user: '1069',
        call_start: '2026-03-25T14:32:00',
        duration: 135,
        disposition: 'ANSWERED',
        direction: 'inbound',
        trunk: 'ukrtelecom',
        recording_path: '/rec/1.wav',
    },
    {
        id: 'c2',
        src_user: '1042',
        dst_user: '0671234567',
        call_start: '2026-03-25T14:28:00',
        duration: 342,
        disposition: 'ANSWERED',
        direction: 'outbound',
        trunk: 'ukrtelecom',
    },
    {
        id: 'c3',
        src_user: '0509876543',
        dst_user: '1088',
        call_start: '2026-03-25T14:15:00',
        duration: 0,
        disposition: 'NO ANSWER',
        direction: 'inbound',
        trunk: 'goip-sim1',
    },
    {
        id: 'c4',
        src_user: '0441234567',
        dst_user: '1088',
        call_start: '2026-03-25T14:02:00',
        duration: 0,
        disposition: 'NO ANSWER',
        direction: 'inbound',
        trunk: 'ukrtelecom',
    },
    {
        id: 'c5',
        src_user: '1081',
        dst_user: '1079',
        call_start: '2026-03-25T13:55:00',
        duration: 93,
        disposition: 'ANSWERED',
        direction: 'internal',
    },
    {
        id: 'c6',
        src_user: '0679999999',
        dst_user: '1088',
        call_start: '2026-03-25T13:41:00',
        duration: 0,
        disposition: 'NO ANSWER',
        direction: 'inbound',
        trunk: 'goip-sim2',
    },
    {
        id: 'c7',
        src_user: '1079',
        dst_user: '0442908000',
        call_start: '2026-03-25T13:30:00',
        duration: 497,
        disposition: 'ANSWERED',
        direction: 'outbound',
        trunk: 'ukrtelecom',
    },
    {
        id: 'c8',
        src_user: '1069',
        dst_user: '1042',
        call_start: '2026-03-25T12:10:00',
        duration: 180,
        disposition: 'ANSWERED',
        direction: 'internal',
    },
];

export const MOCK_RING_GROUPS: MockRingGroup[] = [
    { extension: '601', name: 'Державний контроль на кордоні', strategy: 'simultaneous', members: 8 },
    { extension: '602', name: 'Служба діловодства', strategy: 'cyclic', members: 3 },
    { extension: '603', name: 'Господарське забезпечення', strategy: 'cyclic', members: 2 },
    { extension: '604', name: 'Бухгалтерія', strategy: 'simultaneous', members: 3 },
    { extension: '605', name: 'Публічні закупівлі', strategy: 'simultaneous', members: 2 },
    { extension: '606', name: 'Управління роботи з персоналом', strategy: 'simultaneous', members: 3 },
    { extension: '607', name: 'Правове забезпечення', strategy: 'simultaneous', members: 2 },
    { extension: '608', name: 'Внутрішній аудит', strategy: 'simultaneous', members: 0 },
    { extension: '609', name: 'Запобігання та виявлення корупції', strategy: 'simultaneous', members: 1 },
    { extension: '610', name: 'Оператор (Натисніть 0)', strategy: 'simultaneous', members: 3 },
];

export const MOCK_DEPARTMENTS: MockDepartment[] = [
    { id: 'd1', name: 'Київ', member_count: 11 },
    { id: 'd2', name: 'Хмельницький', member_count: 7 },
    { id: 'd3', name: 'Чернігів', member_count: 6 },
    { id: 'd4', name: 'Могилів-Подільський', member_count: 5 },
    { id: 'd5', name: 'Конотоп', member_count: 4 },
    { id: 'd6', name: 'Житомир', member_count: 3 },
    { id: 'd7', name: 'Суми', member_count: 3 },
    { id: 'd8', name: 'Вінниця', member_count: 3 },
    { id: 'd9', name: 'Біла Церква', member_count: 3 },
    { id: 'd10', name: 'Сіверський', member_count: 3 },
    { id: 'd11', name: 'Овруч', member_count: 2 },
    { id: 'd12', name: 'Бориспіль', member_count: 2 },
    { id: 'd13', name: 'Тростянець', member_count: 2 },
];

export const MOCK_TRUNK_STATS: Record<string, MockTrunkStats> = {
    ukrtelecom: { today_calls: 47, answered: 38, missed: 9, avg_duration: 142, total_duration: 5396, status: 'active' },
    goip: { today_calls: 23, answered: 19, missed: 4, avg_duration: 98, total_duration: 1862, status: 'active' },
};

export const MOCK_TRUNKS: MockTrunk[] = [
    { id: 'ukrtelecom', name: 'Укртелеком (PSTN)', host: '195.5.0.215', protocol: 'UDP', status: 'active' },
    { id: 'goip', name: 'GoIP-4 (GSM Gateway)', host: '192.168.30.165', protocol: 'SIP', status: 'active' },
];

export let MOCK_OFFICE_HOURS: MockOfficeHours = {
    schedule: {
        mon: { enabled: true, start: '09:00', end: '18:00' },
        tue: { enabled: true, start: '09:00', end: '18:00' },
        wed: { enabled: true, start: '09:00', end: '18:00' },
        thu: { enabled: true, start: '09:00', end: '18:00' },
        fri: { enabled: true, start: '09:00', end: '16:45' },
        sat: { enabled: false, start: '09:00', end: '13:00' },
        sun: { enabled: false, start: '09:00', end: '13:00' },
    },
    exceptions: [
        { date: '2026-01-01', name: 'Новий рік', type: 'holiday' },
        { date: '2026-01-07', name: 'Різдво', type: 'holiday' },
        { date: '2026-03-08', name: 'Міжнародний жіночий день', type: 'holiday' },
        { date: '2026-05-01', name: 'День праці', type: 'holiday' },
        { date: '2026-06-28', name: 'День Конституції', type: 'holiday' },
        { date: '2026-08-24', name: 'День Незалежності', type: 'holiday' },
        { date: '2026-10-14', name: 'День захисників і захисниць', type: 'holiday' },
        { date: '2026-12-25', name: 'Різдво (25 грудня)', type: 'holiday' },
    ],
};

export const MOCK_SYSTEM: MockSystem = {
    services: { kamailio: 'active', asterisk: 'active', rtpengine: 'active', postgresql: 'active', api: 'active' },
    active_calls: 3,
    registered_users: 48,
    total_users: 152,
    disk_recordings: { used_gb: 12.4, total_gb: 100, path: '/var/spool/asterisk/monitor' },
    version: 'NIR PBX 1.0.0',
    components: { kamailio: '6.0', asterisk: '16 LTS', rtpengine: 'latest', postgresql: '16' },
    domain: 'sip.nir.gov.ua',
    internal_ip: '192.168.30.138 (eth0)',
    external_ip: '192.168.30.137 (eth1)',
};

export let MOCK_VOICEMAIL: MockVoicemail = {
    enabled: true,
    max_message_length: 180,
    max_messages: 50,
    min_message_length: 3,
    max_greeting_length: 60,
    format: 'wav',
    email_notifications: true,
    email_body: true,
    delete_after_email: false,
    timezone: 'Europe/Kyiv',
    operator_extension: '1069',
    context: 'default',
};

export function mockRequest(path: string, options: RequestOptions = {}): Promise<any> {
    return new Promise((resolve) => {
        setTimeout(() => {
            // Strip trailing slash and query string for easier matching
            const cleanPath = path.replace(/\/?\?.*$/, '').replace(/\/$/, '');

            // Trunk stats: /trunks/{id}/stats
            const trunkStatsMatch = cleanPath.match(/^\/trunks\/(\w+)\/stats/);
            if (trunkStatsMatch) {
                resolve(MOCK_TRUNK_STATS[trunkStatsMatch[1]] || MOCK_TRUNK_STATS.ukrtelecom);
                return;
            }

            if (cleanPath.startsWith('/presence/bulk')) {
                // Parse extensions from query string
                const match = path.match(/extensions=([^&]+)/);
                const exts = match ? decodeURIComponent(match[1]).split(',') : [];
                const result: Record<string, string> = {};
                exts.forEach((ext) => {
                    const user = MOCK_USERS.find((u) => u.extension === ext);
                    result[ext] = user ? user.presence_state || 'available' : 'offline';
                });
                resolve(result);
            } else if (cleanPath.startsWith('/presence/')) {
                // Single presence GET or PUT
                if (options.method === 'PUT') resolve({ status: 'ok' });
                else resolve({ state: 'available', status_text: null });
            } else if (cleanPath.startsWith('/forwarding/')) {
                // Forwarding rules per extension or rule deletion
                if (options.method === 'POST') resolve({ id: 'mock-' + Date.now(), status: 'ok' });
                else if (options.method === 'DELETE') resolve({ status: 'ok' });
                else {
                    const ext = cleanPath.split('/forwarding/')[1];
                    const u = MOCK_USERS.find((u) => u.extension === ext);
                    if (u && u.fwd_target) {
                        resolve([
                            {
                                id: 'mock-' + ext,
                                presence_states: ['available', 'busy', 'dnd', 'away'],
                                schedule: 'always',
                                trigger_type: 'no_answer',
                                no_answer_timeout: u.fwd_timeout || 20,
                                action: 'forward',
                                target: u.fwd_target,
                                priority: 10,
                                is_active: true,
                            },
                        ]);
                    } else {
                        resolve([]);
                    }
                }
            } else if (cleanPath.startsWith('/users')) {
                // Single user or list
                const uidMatch = cleanPath.match(/^\/users\/(\w+)$/);
                if (uidMatch) {
                    const user = MOCK_USERS.find((u) => u.id === uidMatch[1]);
                    resolve(user || MOCK_USERS[0]);
                } else {
                    resolve({ items: MOCK_USERS, total: 152, page: 1, page_size: 50 });
                }
            } else if (cleanPath.startsWith('/departments')) {
                if (options.method === 'PUT' || options.method === 'POST' || options.method === 'DELETE') {
                    resolve({ status: 'ok' });
                } else {
                    const deptMembersMatch = cleanPath.match(/^\/departments\/(\w+)\/members/);
                    if (deptMembersMatch) {
                        const dept = MOCK_DEPARTMENTS.find((d) => d.id === deptMembersMatch[1]);
                        const deptName = dept ? dept.name : '';
                        resolve(MOCK_USERS.filter((u) => u.department_name === deptName));
                    } else {
                        resolve(MOCK_DEPARTMENTS);
                    }
                }
            } else if (cleanPath.startsWith('/cdr')) resolve({ items: MOCK_CDR, total: 284, page: 1 });
            else if (cleanPath.startsWith('/ring-groups')) {
                if (options.method === 'POST') resolve({ id: 'rg-new-' + Date.now(), status: 'ok' });
                else if (options.method === 'PUT') resolve({ status: 'ok' });
                else if (options.method === 'DELETE') resolve({ status: 'ok' });
                else resolve(MOCK_RING_GROUPS);
            } else if (cleanPath.startsWith('/address-book'))
                resolve(
                    MOCK_USERS.map((u) => ({
                        display_name: u.full_name,
                        internal_number: u.extension,
                        position: u.position,
                        department_name: u.department_name,
                        email: u.email,
                        presence_state: u.presence_state,
                    })),
                );
            else if (cleanPath === '/trunks') resolve(MOCK_TRUNKS);
            else if (cleanPath === '/office-hours' && options.method === 'PUT') {
                if (options.body)
                    MOCK_OFFICE_HOURS = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
                resolve(MOCK_OFFICE_HOURS);
            } else if (cleanPath.startsWith('/office-hours/exceptions') && options.method === 'POST') {
                resolve({ status: 'ok' });
            } else if (cleanPath.startsWith('/office-hours/exceptions') && options.method === 'DELETE') {
                resolve({ status: 'ok' });
            } else if (cleanPath === '/office-hours') resolve(MOCK_OFFICE_HOURS);
            else if (cleanPath === '/voicemail/config' && options.method === 'PUT') {
                if (options.body)
                    MOCK_VOICEMAIL = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
                resolve(MOCK_VOICEMAIL);
            } else if (cleanPath === '/voicemail/config') resolve(MOCK_VOICEMAIL);
            else if (cleanPath === '/system/status') resolve(MOCK_SYSTEM);
            else if (cleanPath === '/system') resolve(MOCK_SYSTEM);
            else if (cleanPath.startsWith('/system/health')) resolve({ status: 'ok' });
            else if (cleanPath.startsWith('/system/services')) resolve(MOCK_SYSTEM.services);
            else if (cleanPath.startsWith('/webrtc/credentials'))
                resolve({
                    ws_uri: 'wss://sip.nir.gov.ua:8443',
                    sip_uri: 'sip:1069@sip.nir.gov.ua',
                    display_name: 'Коновалюк Дмитро Вікторович',
                    authorization_user: '1069',
                    password: 'mock-dev-password',
                    auth_type: 'digest',
                    stun_servers: ['stun:stun.l.google.com:19302'],
                });
            else resolve({});
        }, 100);
    });
}
